import { createContext, useContext } from "react";

import { useScheduleController } from "../../../features/planning/schedule/state/useScheduleController";

/**
 * Schedule page state + handlers (data load, timeline, resize, drag-move, create flows).
 * Implementation lives in {@link useScheduleController} under `features/planning/schedule/state/`.
 *
 * @typedef {import('../../../features/planning/schedule/state/scheduleContextValue').ScheduleContextValue} ScheduleContextValue
 */

/** @type {import('react').Context<ScheduleContextValue|null>} */
export const ScheduleContext = createContext(null);

/**
 * Read schedule page state and handlers from {@link ScheduleProvider}.
 *
 * @returns {ScheduleContextValue}
 * @throws {Error} When used outside `ScheduleProvider`.
 */
export function useScheduleContext() {
  const context = useContext(ScheduleContext);
  if (!context) {
    throw new Error(
      "useScheduleContext must be used within a ScheduleProvider"
    );
  }
  return context;
}

/**
 * Wrap `/planning/schedule` (and any subtree that calls {@link useScheduleContext}).
 *
 * @param {Object} props
 * @param {import('react').ReactNode} props.children
 * @param {boolean} [props.compact=false] Whether to use compact schedule chrome.
 * @param {boolean} [props.readOnly=false] Whether schedule mutation UI/actions are disabled.
 * @param {Object|null} [props.singleProject=null] Optional project row used to scope schedule data.
 * @param {Object|null} [props.defaultFilters=null] Optional mount-time filter seeds.
 * @param {Date|null} [props.initialTimelineStart=null] Optional timeline week-start seed.
 * @param {Object} [props.options] Additional schedule controller options.
 * @returns {JSX.Element}
 */
export function ScheduleProvider({
  children,
  compact = false,
  readOnly = false,
  singleProject = null,
  defaultFilters = null,
  initialTimelineStart = null,
  options = {},
}) {
  const value = useScheduleController({
    ...options,
    compact: options.compact ?? compact,
    readOnly: options.readOnly ?? readOnly,
    singleProject: options.singleProject ?? singleProject,
    defaultFilters: options.defaultFilters ?? defaultFilters,
    initialTimelineStart:
      options.initialTimelineStart ?? initialTimelineStart,
  });

  return (
    <ScheduleContext.Provider value={value}>{children}</ScheduleContext.Provider>
  );
}

export default ScheduleProvider;
